/* eslint-disable no-restricted-imports */
/* eslint-disable @typescript-eslint/naming-convention */
import { useEffect, useState } from "react";

import { Modal } from "antd";
import { Formik } from "formik";

import { Caret, NavigationButtons } from "common/lib/components";
import ChartContainer from "domains/altCustody/components/ChartContainer";
import AddChartButton from "domains/altCustody/components/AddChartButton";
import EditChartModal from "domains/altCustody/components/EditChartModal";
import { IChart } from "domains/altCustody/shared/types";
import { formatChartTitleToHumanReadable, prepareChartData } from "domains/altCustody/shared/utils/helpers";
import { tableDataToDataSource } from "./InvestmentTable";
import "../portfolioManager.scss";
import { Form, Select } from "common/lib/formik-antd";
import { ChartType } from "domains/altCustody/shared/constants";
import { chartDataOptions } from "domains/altCustody/components/EditChartModal/constants";
import { currencyFormatter } from "domains/liquidity/shared/utils/inputValueFormatter";

interface InvestmentChartsProps {
    onChangeActiveChart: any; // Asumiendo que es una función que recibe un key
    chartData: any[]; // Tipo de datos, asumiendo que es un array de objetos genéricos. Cambia según sea necesario.
  }

const chartTypeOptions = Object.values(ChartType).map((type) => ({
    label: type[ 0 ].toUpperCase() + type.slice(1),
    value: type,
}));

const InvestmentCharts = ({ onChangeActiveChart, chartData, charts, onChange, handleSliceFilter }: any) => {
    const [ selectedChart, setSelectedChart ] = useState<any>({ });
    const [ activeChartInfo, setActiveChartInfo ] = useState<{ chart?: IChart; activeIndex?: number }>({});
    const [ isModalOpen, setIsModalOpen ] = useState(false);
    
    const handleOpenModal = () => {
        setIsModalOpen(true);
    }

    const handeCloseModal = () => {
      setIsModalOpen(false);
      setSelectedChart({ })
    }

    const extractNumbers = (str: string): string => {
      return str.replace(/\D+/g, ''); // Elimina todo lo que NO sea dígito (\D)
    };

    const onChartFragmentClick = (activeChartInfo:any) => {
      setActiveChartInfo(activeChartInfo);
      if (activeChartInfo) {
        if (activeChartInfo.entry.name.includes('Investor NAV')) {
          const dirtyString = activeChartInfo.entry.name
          const isNegative = activeChartInfo.entry.isNegative
          const cleanNumbers = extractNumbers(dirtyString) || activeChartInfo.entry.total_nav
          const value = isNegative
            ? `-${ cleanNumbers }`
            : cleanNumbers
          handleSliceFilter(value, activeChartInfo.chart.dataKey)
        } else {
          handleSliceFilter(activeChartInfo.entry.name, activeChartInfo.chart.dataKey)
        }
      } else {
        handleSliceFilter('', '')
      }
    }

    const  handleRemoveClick = () => {
      if (Object.keys(selectedChart).length) {
        const updatedCharts = [ ...charts ].filter(({ key }) => {
          return selectedChart.key !== key
        })
        onChange(updatedCharts)
      }
      handeCloseModal()
    }

    const  onEditChart = (params: any) => {
      setSelectedChart(params)
      handleOpenModal()
    }


    const handleSaveClick = (params: IChart) => {
        const { type, dataKey } = params
        let newChartData
        if (Object.keys(selectedChart).length) {
          const newCharts = [ ...charts ]

          // Buscamos la gráfica que coincida con el `key` de `selectedChart` y la actualizamos
          const updatedCharts = newCharts.map(chart => {
              if (chart.key === selectedChart.key) {
                  // Si encontramos la gráfica que se está editando, la actualizamos
                  return {
                      ...chart,
                      title: formatChartTitleToHumanReadable(dataKey),
                      type,
                      dataKey,
                      data: prepareChartData({
                          data: tableDataToDataSource(chartData),
                          dataKey,
                      }),
                  };
              }
              return chart; // Si no es la gráfica que estamos editando, la dejamos igual
          });
  
          updatedCharts.forEach(chart => {
            // Calcular la suma total de total_nav para la gráfica
            const totalNavSum = chart.data.reduce((sum: any, item: { total_nav: any; }) => sum + item.total_nav, 0);
          
            // Asegurarse de que el totalNavSum no sea 0 para evitar división por 0
            if (totalNavSum === 0) {
              // Si totalNavSum es 0, asignamos 0 a todos los values
              chart.data = chart.data.map((item: any) => ({
                ...item,
                value: 0,
              }));
            } else {
              // Calcular el porcentaje de total_nav para cada slice
              chart.data = chart.data.map((item: { total_nav: number; }) => {
                const percentage = (item.total_nav / totalNavSum) * 100;
                return {
                  ...item,
                  value: percentage, // Reemplazar 'value' por el porcentaje
                };
              });
            }
          
            // Normalizar los valores de 'value' para asegurarse de que la suma sea 100
            const sumOfValues = chart.data.reduce((sum: any, item: { value: any; }) => sum + item.value, 0);
          
            if (sumOfValues !== 100) {
              // Si la suma no es 100, ajustar los valores proporcionalmente
              const adjustmentFactor = 100 / sumOfValues;
          
              chart.data = chart.data.map((item: { value: number; }) => ({
                ...item,
                value: item.value * adjustmentFactor,
              }));
            }
          });
  
          // Establecemos el nuevo estado de las gráficas con la gráfica editada
          onChange(updatedCharts);
        } else {
          newChartData = prepareChartData({
            data: tableDataToDataSource(chartData),
            dataKey,
          });
          const updatedCharts = [ ...charts, {
            key: Math.random(),
            title: formatChartTitleToHumanReadable(dataKey),
            type,
            dataKey,
            data: newChartData,
          } ]

          updatedCharts.forEach(chart => {
            // Calcular la suma total de total_nav para la gráfica
            const totalNavSum = chart.data.reduce((sum: any, item: { total_nav: any; }) => sum + item.total_nav, 0);
          
            // Asegurarse de que el totalNavSum no sea 0 para evitar división por 0
            if (totalNavSum === 0) {
              // Si totalNavSum es 0, asignamos 0 a todos los values
              chart.data = chart.data.map((item: any) => ({
                ...item,
                value: 0,
              }));
            } else {
              // Calcular el porcentaje de total_nav para cada slice
              chart.data = chart.data.map((item: { total_nav: number; }) => {
                const percentage = (item.total_nav / totalNavSum) * 100;
                return {
                  ...item,
                  value: percentage, // Reemplazar 'value' por el porcentaje
                };
              });
            }
          
            // Normalizar los valores de 'value' para asegurarse de que la suma sea 100
            const sumOfValues = chart.data.reduce((sum: any, item: { value: any; }) => sum + item.value, 0);
          
            if (sumOfValues !== 100) {
              // Si la suma no es 100, ajustar los valores proporcionalmente
              const adjustmentFactor = 100 / sumOfValues;
          
              chart.data = chart.data.map((item: { value: number; }) => ({
                ...item,
                value: item.value * adjustmentFactor,
              }));
            }
          });
          onChange(updatedCharts)
        }
        handeCloseModal(); 
    };

    useEffect(() => {
      onChangeActiveChart(activeChartInfo)
    }, [ activeChartInfo ])
    
    useEffect(() => {
        const updatedCharts = [ ...charts ].map(chart => {
          const formattedChartData = tableDataToDataSource(chartData)
          const chartData_ = {
            ...chart,
            data: Object.values(
              formattedChartData.reduce((acc: any, chart_: any) => {
                const isFundInvestment = chart.dataKey === "fund_investment_nav" || chart.dataKey === "investor_nav";
                const nav = parseFloat(chart_[ "investor_nav" ] || "0");
                const originalValue = chart_[ "investor_nav" ]; // Guardamos el valor original

                if (isFundInvestment) {
                  // Cambiamos la clave de agrupación al valor numérico en lugar de fund_name
                  const key = nav.toString(); 
                  
                  if (!acc[ key ]) {
                    acc[ key ] = {
                      name: `Investor NAV (${ currencyFormatter(nav) })`, // Nombre formateado desde el inicio
                      total_nav: 0,
                      count: 0,
                      rawValues: [],
                      originalValues: [], // Guardamos valores originales para referencia
                    };
                  }
          
                  acc[ key ].total_nav += nav;
                  acc[ key ].count += 1;
                  acc[ key ].rawValues.push(nav);
                  acc[ key ].originalValues.push(originalValue); // Almacenamos el valor original
                  
                  // Actualizamos el name si hay múltiples valores
                  if (acc[ key ].count > 1) {
                    acc[ key ].name = `Investor NAV (${ currencyFormatter(nav) } × ${ acc[ key ].count })`;
                  }
                } else {
                  const key = chart_[ chart.dataKey ];
                  if (!key) return acc;
          
                  if (!acc[ key ]) {
                    acc[ key ] = {
                      name: key,
                      total_nav: 0,
                      count: 0,
                    };
                  }
          
                  acc[ key ].total_nav += nav;
                  acc[ key ].count += 1;
                }
          
                return acc;
              }, {}),
            ).map((item: any) => {
              const { name, total_nav, count } = item;
          
              if ((chart.dataKey === "investor_nav" || chart.dataKey === "fund_investment_nav") && item.rawValues) {
                const first = item.rawValues[ 0 ];
                const showIndividual = item.rawValues.length > 1 || first !== total_nav;
                const isNegative = Number(total_nav) < 0;
          
                return {
                  name: showIndividual
                    ? `Investor NAV (${ isNegative ? '- ' : '' }${ currencyFormatter(first) })`
                    : 'Investor NAV',
                  value: count,
                  barValue: first,
                  total_nav: Math.abs(total_nav),
                  isNegative,
                };
              }
          
              return {
                name,
                value: count,
                total_nav: Math.abs(total_nav),
                isNegative: Number(total_nav) < 0,
              };
            }),
          };
          
          return chartData_
        });
        updatedCharts.forEach(chart => {
          // Calcular la suma total de total_nav para la gráfica
          const totalNavSum = chart.data.reduce((sum: any, item: { total_nav: any; }) => sum + item.total_nav, 0);
        
          // Asegurarse de que el totalNavSum no sea 0 para evitar división por 0
          if (totalNavSum === 0) {
            // Si totalNavSum es 0, asignamos 0 a todos los values
            chart.data = chart.data.map((item: any) => ({
              ...item,
              value: 0,
            }));
          } else {
            // Calcular el porcentaje de total_nav para cada slice
            chart.data = chart.data.map((item: { total_nav: number; }) => {
              const percentage = (item.total_nav / totalNavSum) * 100;
              return {
                ...item,
                value: percentage, // Reemplazar 'value' por el porcentaje
              };
            });
          }
        
          // Normalizar los valores de 'value' para asegurarse de que la suma sea 100
          const sumOfValues = chart.data.reduce((sum: any, item: { value: any; }) => sum + item.value, 0);
        
          if (sumOfValues !== 100) {
            // Si la suma no es 100, ajustar los valores proporcionalmente
            const adjustmentFactor = 100 / sumOfValues;
        
            chart.data = chart.data.map((item: { value: number; }) => ({
              ...item,
              value: item.value * adjustmentFactor,
            }));
          }
        });


        onChange(updatedCharts)
    }, [ chartData ])

    return (
        <>
          <div id="charts-list-container" className="ben-charts-list-container">
            {charts.map((chart: any) => (
              <ChartContainer
                onEdit={ () => onEditChart(chart) }
                key={ chart.key }
                chart={ chart }
                activeIndex={ activeChartInfo?.chart?.key === chart.key ? 0 : undefined }
                tableDataSource={ chartData }
                onChartFragmentClick={ onChartFragmentClick }
              />
            ))}
    
            {charts.length <= 10 && <AddChartButton onClick={ handleOpenModal } />}
          </div>
    
          {isModalOpen && (
            <Modal centered footer={ null }  open={ isModalOpen } onCancel={ handeCloseModal }>
                <div className="ben-edit-chart-modal-content">
                    <h1>Create/Update Chart</h1>
                    <div className="ben-form-container">
                        <Formik
                        initialValues={ selectedChart }
                        onSubmit={ handleSaveClick }
                        enableReinitialize
                        validateOnMount
                        >
                        {({ isValid, dirty }) => {
                            return (
                            <Form layout="vertical" style={ { margin: "0 auto 0 0" } } className="ben-custody-account-id-form">
                                <Form.Item name="type" label={ "Chart Type" } className="ben-input">
                                <Select
                                    showSearch
                                    name="type"
                                    optionFilterProp="label"
                                    suffixIcon={ <Caret style={ { transform: "rotate(90deg)" } } /> }
                                    options={ chartTypeOptions }
                                    // @ts-ignore
                                    autoComplete="nope"
                                />
                                </Form.Item>
            
                                <Form.Item name="dataKey" label={ "Chart Data" } className="ben-input">
                                <Select
                                    showSearch
                                    name="dataKey"
                                    optionFilterProp="label"
                                    suffixIcon={ <Caret style={ { transform: "rotate(90deg)" } } /> }
                                    options={ chartDataOptions }
                                    // @ts-ignore
                                    autoComplete="nope"
                                />
                                </Form.Item>
            
                                <NavigationButtons
                                    disabled={ !isValid || !dirty }
                                    backButtonText="Cancel"
                                    submitButtonText="Save"
                                    skipButtonText="Remove"
                                    hideSkip={ !Object.keys(selectedChart).length }
                                    onSkip={ handleRemoveClick }
                                    onBack={ handeCloseModal }
                                />
                            </Form>
                            );
                        }}
                        </Formik>
                    </div>
                </div>
            </Modal>
          )}
        </>
      );
}

export default InvestmentCharts
